import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Shield } from "lucide-react";
import { Redirect, Link } from "wouter";
import logoBlack from "@assets/TORK_Logo_Black_1779647609157.png";
import logoWhite from "@assets/TORK_Logo_White_1779647609157.png";

export default function AdminLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { login, isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Redirect to="/dashboard" />;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    login.mutate({ email, password });
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-6" data-testid="page-admin-login">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <img src={logoBlack} alt="Tork" className="h-32 block dark:hidden" data-testid="logo-admin-login" />
          <img src={logoWhite} alt="Tork" className="h-32 hidden dark:block" />
        </div>

        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border bg-muted/40 text-xs font-medium text-muted-foreground uppercase tracking-wider mb-4">
            <Shield className="h-3.5 w-3.5" />
            Admin Access
          </div>
          <h1 className="text-2xl font-bold text-foreground" data-testid="text-admin-headline">
            Sign in to Tork Admin
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Staff only. Customers should use Google sign-in.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="admin-email" className="text-xs font-semibold tracking-wider uppercase text-muted-foreground">
                Email
              </Label>
              <Input
                id="admin-email"
                type="email"
                placeholder="admin@digitaldomination.ai"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="input-admin-email"
                className="h-12 text-base"
                autoComplete="username"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin-password" className="text-xs font-semibold tracking-wider uppercase text-muted-foreground">
                Password
              </Label>
              <Input
                id="admin-password"
                type="password"
                placeholder="********"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="input-admin-password"
                className="h-12 text-base"
                autoComplete="current-password"
              />
            </div>
          </div>

          {login.isError && (
            <p className="text-sm text-destructive" data-testid="text-admin-login-error">
              {login.error?.message?.includes("401") ? "Invalid email or password" : login.error?.message}
            </p>
          )}

          <Button
            type="submit"
            disabled={login.isPending}
            className="w-full h-12 bg-[#F6642A] hover:bg-[#F6642A]/90 text-base font-semibold"
            data-testid="button-admin-login"
          >
            {login.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            Not an admin?{" "}
            <Link
              href="/login"
              className="font-semibold text-primary hover:underline"
              data-testid="link-customer-login"
            >
              Customer sign-in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
